import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { FaHashtag, FaChartLine, FaSpinner } from "react-icons/fa";
import { SurveyContext } from "../context/SurveyContext";

const TrendingHashtags = () => {
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { condition } = useContext(SurveyContext);
  const navigate = useNavigate();

  useEffect(() => {
    const loadTrending = async () => {
      try {
        setLoading(true);
        const response = await api.get("/search/trending", {
          params: { condition, limit: 6, timeframe: "24h" }
        });
        setTrending(response.data.trending || []);
      } catch (error) {
        console.error("Trending error:", error);
        setError("Failed to load trending hashtags");
      } finally {
        setLoading(false);
      }
    };

    loadTrending();
  }, [condition]);

  const handleHashtagClick = (hashtag) => {
    const hashtagQuery = hashtag.startsWith('#') ? hashtag : `#${hashtag}`;
    navigate(`/search?q=${encodeURIComponent(hashtagQuery)}&type=all`);
  };

  if (loading) {
    return (
      <div className="spiders-card p-4">
        <div className="flex items-center justify-center space-x-2 text-spiders-text-muted">
          <FaSpinner className="animate-spin" />
          <span>Loading trends...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="spiders-card p-4 text-spiders-error">{error}</div>
    );
  }

  return (
    <div className="spiders-card overflow-hidden">
      <div className="p-4 border-b border-spiders-border">
        <h2 className="text-xl font-bold text-spiders-text-primary flex items-center">
          <FaChartLine className="w-5 h-5 mr-2 text-spiders-accent-400" />
          What's happening
        </h2>
      </div>

      {trending.length === 0 ? (
        <div className="p-4 text-spiders-text-muted text-center">
          No trends available
        </div>
      ) : (
        <div>
          {trending.map((hashtag, index) => (
            <button
              key={hashtag._id}
              onClick={() => handleHashtagClick(hashtag.name || hashtag._id)}
              className="w-full px-4 py-4 border-b border-spiders-border hover:bg-spiders-bg-elevated/30 transition-colors duration-200 text-left last:border-b-0"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-spiders-text-muted text-sm">#{index + 1} · Trending in Research</span>
                    {hashtag.isRising && (
                      <div className="flex items-center space-x-1">
                        <FaChartLine className="w-3 h-3 text-spiders-success" />
                        <span className="text-spiders-success text-xs">Rising</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2 mb-1">
                    <FaHashtag className="text-spiders-accent-400 w-4 h-4 flex-shrink-0" />
                    <h3 className="text-spiders-text-primary font-bold text-lg">
                      {hashtag.name || hashtag._id}
                    </h3>
                  </div>

                  <p className="text-spiders-text-muted text-sm">
                    {hashtag.usageCount.toLocaleString()} posts
                    {hashtag.trendingScore > hashtag.usageCount && (
                      <span className="ml-2 text-spiders-accent-400">
                        · Hot topic
                      </span>
                    )}
                  </p>
                </div>

                <div className="flex-shrink-0 ml-3">
                  <div className="w-3 h-3 rounded-full bg-spiders-primary-400"></div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="p-4 border-t border-spiders-border">
        <button
          onClick={() => navigate('/search')}
          className="text-spiders-primary-400 hover:text-spiders-primary-300 text-sm font-medium transition-colors"
        >
          Show more
        </button>
      </div>
    </div>
  );
};

export default TrendingHashtags;
